import { UiSchema } from '@rjsf/utils'
import { omit, cloneDeep } from 'lodash'
import { AnyObject } from '@feedyou/utils'
import { BotFeatures } from '@feedyou-ai/feedbot-schema'
import produce from 'immer'

import { useIsTelephony } from '../../../store/flow'
import { HIDDEN_WIDGET, WITHOUT_LABEL } from '../constants'
import { useOpenModalDescriptor, ModalDescriptorForType } from '../../../store/modals'

export function useBaseSubstepUiSchema(): UiSchema {
  return {
    dialog: { 'ui:widget': 'DialogCreatableSelectWidget' },
    action: {
      knowledge: {
        knowledgeBaseId: { 'ui:widget': 'KnowledgeBaseSelectWidget' }
      }
    }
  }
}

export function useBaseSchema(stepSchema: AnyObject): AnyObject {
  const modalDescriptor = useOpenModalDescriptor() as ModalDescriptorForType<'step_settings'>

  const schema = cloneDeep(stepSchema)
  if (modalDescriptor.openSection === 'json') {
    return schema
  }

  return { ...schema, properties: omit(schema.properties, 'type') }
}

export function useBaseStepSchema(
  stepSchema: AnyObject,
  sdkFeatures: BotFeatures[] = []
): AnyObject {
  const modalDescriptor = useOpenModalDescriptor() as ModalDescriptorForType<'step_settings'>
  return getBaseStepSchema(stepSchema, modalDescriptor, sdkFeatures)
}

export function getBaseStepSchema(
  stepSchema: AnyObject,
  openModal: ModalDescriptorForType<'step_settings'>,
  sdkFeatures: BotFeatures[] = []
) {
  const stepConditionalityEnabled =
    window.Cypress || sdkFeatures?.includes(BotFeatures.ALLOW_ALL_STEP_WITH_DISABLE_PROPERTY)

  return produce(stepSchema, draft => {
    if (openModal.openSection !== 'form') {
      return
    }

    delete draft.properties.type
    delete draft.properties.mutations
    draft.required = []

    if (!stepConditionalityEnabled) {
      delete draft.properties.disabled
      delete draft.properties.condition
      delete draft.properties.conditionTitle
    }
  })
}

function getInitUiSchema(): UiSchema {
  return {
    body: { 'ui:widget': 'textarea' },
    mutations: {
      'ui:widget': 'hidden',
      'ui:title': false
    },
    bodyPrefix: { 'ui:widget': 'textarea' },
    bodyPostfix: { 'ui:widget': 'textarea' },
    items: {
      test: {
        items: {
          field: { 'ui:widget': 'hidden' }
        }
      },
      set: {
        items: {
          field: { 'ui:widget': 'hidden' }
        }
      }
    }
  }
}

export function useBaseStepUiSchema(sdkFeatures: BotFeatures[] = []): UiSchema {
  const isTelephony = useIsTelephony()

  const uiSchema: UiSchema = {}

  const isAttachmentEnabled = sdkFeatures.includes(BotFeatures.DETECT_ATTACHMENT_TYPE)
  const skipPlaybackWithNextMessage = sdkFeatures.includes(
    BotFeatures.SKIP_CURRENT_PLAYBACK_WITH_NEXT_MESSAGE
  )

  uiSchema.ignoredTrainingNlpUtterances = HIDDEN_WIDGET
  uiSchema.dialog = { 'ui:widget': 'DialogCreatableSelectWidget' }

  if (isAttachmentEnabled) {
    uiSchema.attachment = { contentType: HIDDEN_WIDGET }
  }

  if (isTelephony) {
    if (!skipPlaybackWithNextMessage) {
      uiSchema.telephony = { interruptible: HIDDEN_WIDGET }
    }
    uiSchema.delayAfter = HIDDEN_WIDGET
    uiSchema.delayBefore = HIDDEN_WIDGET
    uiSchema.retryPrompt = { 'ui:widget': 'EditorXmlWidget' }
  } else {
    uiSchema.telephony = HIDDEN_WIDGET
    uiSchema.retryPrompt = { 'ui:widget': 'textarea' }
  }

  uiSchema.storageSummary = {
    rows: { items: { storage: { 'ui:widget': 'StepStorageSummaryWidget' }, ...WITHOUT_LABEL } }
  }

  return uiSchema
}
